/**
 * <DateField /> · <PhoneField /> · <EmailField /> — input primitives
 * with KSA-aware validation + bilingual error messages.
 *
 * Per DESIGN_SYSTEM.md §8 "Form patterns" + commandment 74 (every UI
 * string paired EN+AR). Every field renders a native HTML5 input of
 * the appropriate type so the browser picker (date / email keyboard /
 * tel keyboard on mobile) shows up automatically — the styling sits
 * around the input, not on top of it.
 *
 * Validation runs on blur AND on each change after the first blur, so
 * the user gets feedback as they type a correction. Error text appears
 * below the input in `text-rose-600`.
 *
 * Use directly OR in react-hook-form via `{...register("field")}`.
 */

import { CalendarDays, Mail, Phone } from "lucide-react";
import {
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactNode,
  forwardRef,
  useEffect,
  useId,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// Shared shell — label · input wrapper · error · helper
// ---------------------------------------------------------------------------

interface FieldShellProps {
  id: string;
  label?: ReactNode;
  required?: boolean;
  helper?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}

function FieldShell({ id, label, required, helper, error, children }: FieldShellProps) {
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-text-primary">
          {label}
          {required && (
            <span aria-hidden className="ms-0.5 text-rose-600">
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <p id={`${id}-err`} role="alert" className="text-xs text-rose-600">
          {error}
        </p>
      ) : helper ? (
        <p id={`${id}-help`} className="text-xs text-text-tertiary">
          {helper}
        </p>
      ) : null}
    </div>
  );
}

const baseInputClass =
  "h-10 w-full rounded-md border border-border-default bg-surface-default px-3 text-sm text-text-primary outline-none transition-colors placeholder:text-text-tertiary focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 disabled:bg-surface-muted";

// ---------------------------------------------------------------------------
// <DateField />
// ---------------------------------------------------------------------------

export interface DateFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: ReactNode;
  helper?: ReactNode;
  required?: boolean;
  /** Inclusive lower bound — YYYY-MM-DD. */
  min?: string;
  /** Inclusive upper bound — YYYY-MM-DD. */
  max?: string;
  /** Override the internal error after validation. */
  error?: string;
}

export const DateField = forwardRef<HTMLInputElement, DateFieldProps>(
  function DateField(
    { label, helper, required, min, max, error, onBlur, onChange, id, value, defaultValue, ...rest },
    ref,
  ) {
    const { t } = useTranslation();
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const [touched, setTouched] = useState(false);
    const [localErr, setLocalErr] = useState<string | null>(null);

    const validate = (v: string | undefined): string | null => {
      if (!v) {
        return required
          ? t("forms.error.date_required", "Date is required.")
          : null;
      }
      // Native input gives YYYY-MM-DD; nothing else parses cleanly.
      if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        return t("forms.error.date_invalid", "Use a valid date (YYYY-MM-DD).");
      }
      if (min && v < min) {
        return t("forms.error.date_too_early", "Date is before the allowed range.");
      }
      if (max && v > max) {
        return t("forms.error.date_too_late", "Date is after the allowed range.");
      }
      return null;
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      setTouched(true);
      setLocalErr(validate(e.currentTarget.value));
      onBlur?.(e);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (touched) setLocalErr(validate(e.currentTarget.value));
      onChange?.(e);
    };

    const shown = error ?? localErr ?? undefined;

    return (
      <FieldShell id={inputId} label={label} required={required} helper={helper} error={shown}>
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type="date"
            min={min}
            max={max}
            value={value as string | number | readonly string[] | undefined}
            defaultValue={defaultValue}
            onBlur={handleBlur}
            onChange={handleChange}
            required={required}
            aria-invalid={!!shown}
            aria-describedby={shown ? `${inputId}-err` : helper ? `${inputId}-help` : undefined}
            className={`${baseInputClass} ${shown ? "border-rose-600" : ""} pe-9`}
            {...rest}
          />
          <CalendarDays
            aria-hidden
            className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary"
          />
        </div>
      </FieldShell>
    );
  },
);

// ---------------------------------------------------------------------------
// <PhoneField /> — KSA-aware (+966)
// ---------------------------------------------------------------------------

/**
 * Normalize a Saudi phone number to E.164.
 *
 * Accepts (case-insensitive on spaces / dashes):
 *   - `+966 5X XXX XXXX` / `+96650...`
 *   - `0095...` (international prefix)
 *   - `05XXXXXXXX` (local 10-digit)
 *   - `5XXXXXXXX` (mobile w/o leading zero)
 *   - landlines starting `01x`/`02x`/etc.
 *
 * Returns `null` if the input can't be confidently mapped.
 */
export function normalizeSaudiPhone(raw: string): string | null {
  const trimmed = raw.replace(/[\s\-()]/g, "");
  if (!trimmed) return null;

  // Already E.164 +966?
  if (/^\+966\d{8,9}$/.test(trimmed)) return trimmed;
  if (/^00966\d{8,9}$/.test(trimmed)) return `+${trimmed.slice(2)}`;
  // Local 0-prefixed (`05X...` mobile, `01X...` landline)
  if (/^0\d{9}$/.test(trimmed)) return `+966${trimmed.slice(1)}`;
  // Bare mobile `5XXXXXXXX`
  if (/^5\d{8}$/.test(trimmed)) return `+966${trimmed}`;
  return null;
}

/** True if input parses to E.164 +966 (no length other than 12 or 13 chars). */
export function isValidSaudiPhone(raw: string): boolean {
  const n = normalizeSaudiPhone(raw);
  return n !== null && /^\+966\d{9}$/.test(n);
}

export interface PhoneFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: ReactNode;
  helper?: ReactNode;
  required?: boolean;
  error?: string;
  /** If `true`, validates against the KSA E.164 shape. Default `true`. */
  validateKsa?: boolean;
}

export const PhoneField = forwardRef<HTMLInputElement, PhoneFieldProps>(
  function PhoneField(
    { label, helper, required, error, validateKsa = true, onBlur, onChange, id, ...rest },
    ref,
  ) {
    const { t } = useTranslation();
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const [touched, setTouched] = useState(false);
    const [localErr, setLocalErr] = useState<string | null>(null);

    const validate = (v: string): string | null => {
      if (!v) {
        return required
          ? t("forms.error.phone_required", "Phone number is required.")
          : null;
      }
      if (validateKsa && !isValidSaudiPhone(v)) {
        return t(
          "forms.error.phone_ksa",
          "Enter a Saudi number (+966 5X XXX XXXX or 05X XXX XXXX).",
        );
      }
      return null;
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      setTouched(true);
      setLocalErr(validate(e.currentTarget.value));
      onBlur?.(e);
    };
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (touched) setLocalErr(validate(e.currentTarget.value));
      onChange?.(e);
    };

    const shown = error ?? localErr ?? undefined;

    return (
      <FieldShell
        id={inputId}
        label={label}
        required={required}
        helper={helper ?? t("forms.helper.phone_ksa", "Saudi format: +966 5X XXX XXXX")}
        error={shown}
      >
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            dir="ltr"
            placeholder="+966 5X XXX XXXX"
            onBlur={handleBlur}
            onChange={handleChange}
            required={required}
            aria-invalid={!!shown}
            aria-describedby={shown ? `${inputId}-err` : `${inputId}-help`}
            className={`${baseInputClass} ${shown ? "border-rose-600" : ""} pe-9`}
            {...rest}
          />
          <Phone
            aria-hidden
            className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary"
          />
        </div>
      </FieldShell>
    );
  },
);

// ---------------------------------------------------------------------------
// <EmailField />
// ---------------------------------------------------------------------------

const EMAIL_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export function isValidEmail(raw: string): boolean {
  return EMAIL_RE.test(raw.trim());
}

export interface EmailFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: ReactNode;
  helper?: ReactNode;
  required?: boolean;
  error?: string;
}

export const EmailField = forwardRef<HTMLInputElement, EmailFieldProps>(
  function EmailField(
    { label, helper, required, error, onBlur, onChange, id, ...rest },
    ref,
  ) {
    const { t } = useTranslation();
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const [touched, setTouched] = useState(false);
    const [localErr, setLocalErr] = useState<string | null>(null);

    const validate = (v: string): string | null => {
      if (!v) {
        return required
          ? t("forms.error.email_required", "Email is required.")
          : null;
      }
      if (!isValidEmail(v)) {
        return t("forms.error.email_invalid", "Enter a valid email address.");
      }
      return null;
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      setTouched(true);
      setLocalErr(validate(e.currentTarget.value));
      onBlur?.(e);
    };
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (touched) setLocalErr(validate(e.currentTarget.value));
      onChange?.(e);
    };

    const shown = error ?? localErr ?? undefined;

    // Auto-clear the local error when caller passes an explicit one.
    useEffect(() => {
      if (error) setLocalErr(null);
    }, [error]);

    return (
      <FieldShell id={inputId} label={label} required={required} helper={helper} error={shown}>
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type="email"
            inputMode="email"
            autoComplete="email"
            dir="ltr"
            placeholder="name@example.com"
            onBlur={handleBlur}
            onChange={handleChange}
            required={required}
            aria-invalid={!!shown}
            aria-describedby={shown ? `${inputId}-err` : helper ? `${inputId}-help` : undefined}
            className={`${baseInputClass} ${shown ? "border-rose-600" : ""} pe-9`}
            {...rest}
          />
          <Mail
            aria-hidden
            className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary"
          />
        </div>
      </FieldShell>
    );
  },
);
